import { trackPageView } from '@/app/analytics/trackPageView'

const metadataSelector =
  'title, meta[name="description"], meta[property^="og:"], meta[property^="article:"], meta[name^="twitter:"], link[rel="canonical"], script[type="application/ld+json"]'

type ScrollPosition = { x: number; y: number }

const scrollSaveDelayMs = 200
const isBlogUrl = (url: URL) => url.origin === location.origin && /^\/blog(?:\/[^/]+)?\/?$/.test(url.pathname)
const pageKey = (url: URL) => `${url.pathname}${url.search}`

/** SSRの本文だけを差し替える。初回描画とJavaScript無効時は通常のHTMLを利用する。 */
export const startBlogNavigation = () => {
  if (!document.querySelector('[data-blog-document] main')) return () => {}

  trackPageView(location.pathname + location.search)

  let controller: AbortController | undefined
  let cancelScrollRestore = () => {}
  let loading = false
  let scrollSaveTimer: number | undefined
  let displayedPage: string | undefined = pageKey(new URL(location.href))
  const previousScrollRestoration = history.scrollRestoration
  history.scrollRestoration = 'manual'

  const writeScroll = () => {
    if (loading) return
    try {
      history.replaceState({ ...history.state, blogScroll: { x: scrollX, y: scrollY } }, '', location.href)
    } catch {
      // SafariのHistory API上限に達しても、画面遷移を妨げない。
    }
  }
  const saveScroll = () => {
    if (loading || scrollSaveTimer !== undefined) return
    scrollSaveTimer = window.setTimeout(() => {
      scrollSaveTimer = undefined
      writeScroll()
    }, scrollSaveDelayMs)
  }
  const flushScrollSave = () => {
    if (scrollSaveTimer !== undefined) {
      window.clearTimeout(scrollSaveTimer)
      scrollSaveTimer = undefined
    }
    writeScroll()
  }
  writeScroll()

  const replaceMain = (main: HTMLElement) => {
    main.tabIndex = -1
    document.querySelector('[data-blog-document] main')?.replaceWith(main)
    main.focus({ preventScroll: true })
  }

  const showStatus = (failed: boolean, minimumHeight: number) => {
    const main = document.createElement('main')
    main.style.minHeight = `${minimumHeight}px`
    if (failed) {
      const message = document.createElement('p')
      message.className = 'status'
      message.setAttribute('role', 'alert')
      message.textContent = '記事を読み込めませんでした。時間をおいて再試行してください。'
      main.appendChild(message)
      const retry = document.createElement('button')
      retry.type = 'button'
      retry.dataset.blogRetry = ''
      retry.textContent = '再試行'
      const back = document.createElement('a')
      back.href = '/blog'
      back.className = 'back-link'
      back.textContent = '記事一覧へ戻る'
      main.appendChild(retry)
      main.appendChild(back)
    } else {
      main.setAttribute('aria-busy', 'true')
      const isArticle = /^\/blog\/[^/]+\/?$/.test(location.pathname)
      if (isArticle) {
        const article = document.createElement('article')
        article.className = 'post blog-loading-article'
        const headerSkeleton = document.createElement('div')
        headerSkeleton.className = 'blog-article-header-skeleton'
        headerSkeleton.setAttribute('aria-hidden', 'true')
        for (const className of [
          'blog-breadcrumbs-skeleton',
          'blog-category-skeleton',
          'blog-title-skeleton',
          'blog-date-skeleton',
        ]) {
          const skeleton = document.createElement('div')
          skeleton.className = className
          headerSkeleton.appendChild(skeleton)
        }
        const heroSkeleton = document.createElement('div')
        heroSkeleton.className = 'blog-hero-skeleton'
        heroSkeleton.setAttribute('aria-hidden', 'true')
        const contentSkeleton = document.createElement('div')
        contentSkeleton.className = 'blog-content-skeleton'
        contentSkeleton.setAttribute('aria-hidden', 'true')
        for (const className of [
          'blog-content-skeleton-heading',
          'blog-content-skeleton-line',
          'blog-content-skeleton-line',
          'blog-content-skeleton-line blog-content-skeleton-line-short',
        ]) {
          const skeleton = document.createElement('div')
          skeleton.className = className
          contentSkeleton.appendChild(skeleton)
        }
        const spinner = document.createElement('div')
        spinner.className = 'blog-loading-spinner'
        spinner.setAttribute('aria-hidden', 'true')
        article.appendChild(headerSkeleton)
        article.appendChild(heroSkeleton)
        article.appendChild(spinner)
        article.appendChild(contentSkeleton)
        main.appendChild(article)
      } else {
        const listingSkeleton = document.createElement('div')
        listingSkeleton.className = 'blog-listing-skeleton'
        listingSkeleton.setAttribute('aria-hidden', 'true')
        const heading = document.createElement('div')
        heading.className = 'blog-listing-heading-skeleton'
        const categories = document.createElement('div')
        categories.className = 'blog-listing-categories-skeleton'
        for (let index = 0; index < 4; index += 1) {
          const category = document.createElement('div')
          category.className = 'blog-listing-category-skeleton'
          categories.appendChild(category)
        }
        const cards = document.createElement('div')
        cards.className = 'blog-listing-cards-skeleton'
        for (let index = 0; index < 6; index += 1) {
          const card = document.createElement('div')
          card.className = 'blog-listing-card-skeleton'
          cards.appendChild(card)
        }
        listingSkeleton.appendChild(heading)
        listingSkeleton.appendChild(categories)
        listingSkeleton.appendChild(cards)
        main.appendChild(listingSkeleton)
      }
    }
    replaceMain(main)
  }

  const scrollToDestination = (url: URL, position?: ScrollPosition) => {
    if (position) {
      window.scrollTo(position.x, position.y)
      return
    }
    if (url.hash) {
      try {
        const target = document.getElementById(decodeURIComponent(url.hash.slice(1)))
        if (target) {
          target.scrollIntoView()
          return
        }
      } catch {
        // 不正なエンコードのフラグメントはページ先頭として扱う。
      }
    }
    window.scrollTo(0, 0)
  }

  const navigate = async (url: URL, position?: ScrollPosition) => {
    const currentMain = document.querySelector<HTMLElement>('[data-blog-document] main')
    const minimumHeight = Math.ceil(currentMain?.getBoundingClientRect().height ?? 0)
    cancelScrollRestore()
    controller?.abort()
    const request = new AbortController()
    controller = request
    loading = true
    displayedPage = undefined
    showStatus(false, minimumHeight)
    document.title = '読み込み中… | Shiboruu'
    window.scrollTo(0, 0)
    // 古い記事の検索・共有用情報を新しいURLに残さない。
    document.head.querySelectorAll(metadataSelector).forEach((node) => {
      if (node.tagName !== 'TITLE') node.remove()
    })
    const timeout = window.setTimeout(() => request.abort(), 30_000)
    try {
      const response = await fetch(url.href, { signal: request.signal, headers: { Accept: 'text/html' } })
      if ((!response.ok && response.status !== 404) || !response.headers.get('content-type')?.includes('text/html')) {
        throw new Error('Blog response unavailable')
      }
      if (response.redirected) throw new Error('Unexpected blog redirect')
      const next = new DOMParser().parseFromString(await response.text(), 'text/html')
      if (controller !== request || request.signal.aborted) return
      const main = next.querySelector<HTMLElement>('[data-blog-document] main')
      if (!main || !next.title) throw new Error('Invalid blog document')
      replaceMain(main)
      document.head.querySelectorAll(metadataSelector).forEach((node) => {
        node.remove()
      })
      next.head.querySelectorAll(metadataSelector).forEach((node) => {
        document.head.appendChild(node)
      })
      displayedPage = pageKey(url)
      trackPageView(displayedPage)
      scrollToDestination(url, position)
      // 画像による高さの変化後も履歴・目次の位置を復元する。ユーザーが操作したら補正を止める。
      if (position?.y || url.hash) {
        const restoration = new AbortController()
        const options = { signal: restoration.signal }
        const stopRestoring = () => restoration.abort()
        const timer = window.setTimeout(stopRestoring, 5000)
        restoration.signal.addEventListener('abort', () => window.clearTimeout(timer), { once: true })
        cancelScrollRestore = stopRestoring
        for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
          window.addEventListener(event, stopRestoring, { ...options, passive: true })
        }
        main.querySelectorAll('img').forEach((image) => {
          if (!image.complete) {
            const restore = () => scrollToDestination(url, position)
            image.addEventListener('load', restore, options)
            image.addEventListener('error', restore, options)
          }
        })
      }
    } catch {
      if (controller !== request) return
      showStatus(true, minimumHeight)
      document.title = '記事を読み込めません | Shiboruu'
    } finally {
      window.clearTimeout(timeout)
      if (controller === request) loading = false
    }
  }

  const onClick = (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }
    if (!(event.target instanceof Element)) return
    if (event.target.closest('[data-blog-retry]')) {
      void navigate(new URL(location.href))
      return
    }
    const link = event.target.closest<HTMLAnchorElement>('a[href]')
    if (
      !link ||
      link.hasAttribute('download') ||
      (link.target && link.target !== '_self') ||
      link.relList.contains('external')
    )
      return
    const url = new URL(link.href)
    if (!isBlogUrl(url)) return
    // 目次など同じページのフラグメント移動はブラウザに任せる。
    if (pageKey(url) === displayedPage && url.hash && !loading) return
    event.preventDefault()
    flushScrollSave()
    if (url.href !== location.href) {
      try {
        history.pushState({ blogScroll: { x: 0, y: 0 } }, '', url.href)
      } catch {
        location.assign(url.href)
        return
      }
    }
    void navigate(url)
  }

  const onPopState = (event: PopStateEvent) => {
    cancelScrollRestore()
    const url = new URL(location.href)
    if (!isBlogUrl(url)) {
      location.reload()
      return
    }
    if (!loading && displayedPage === pageKey(url)) {
      scrollToDestination(url, url.hash ? undefined : event.state?.blogScroll)
      return
    }
    void navigate(url, event.state?.blogScroll)
  }

  document.addEventListener('click', onClick)
  window.addEventListener('popstate', onPopState)
  window.addEventListener('scroll', saveScroll, { passive: true })
  return () => {
    cancelScrollRestore()
    controller?.abort()
    controller = undefined
    document.removeEventListener('click', onClick)
    window.removeEventListener('popstate', onPopState)
    window.removeEventListener('scroll', saveScroll)
    if (scrollSaveTimer !== undefined) window.clearTimeout(scrollSaveTimer)
    history.scrollRestoration = previousScrollRestoration
  }
}
